import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Zap, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CategoryCard } from "@/components/category-card";
import { SEO } from "@/components/seo";
import type { Category } from "@shared/schema";

interface GroupPageProps {
  group: string;
}

export default function GroupPage({ group }: GroupPageProps) {

  const { data: categories, isLoading } = useQuery<Category[]>({
    queryKey: ["/api/categories"],
  });

  const filtered = categories?.filter((c) => c.group === group) || [];
  const title = group === "templates" ? "Chart Templates" : "Sierra Chart Indicators";
  const description =
    group === "templates"
      ? "Professional chart templates for Sierra Chart. Ready to import and use in your trading setup."
      : "Custom-built indicators to enhance your Sierra Chart analysis and trading decisions.";

  const seoTitle = group === "templates"
    ? "Sierra Chart Templates - Market Profile, Footprints, Volume Profiles, Heatmaps, DOM"
    : "Sierra Chart Custom Indicators - Orderflow, Volume Sequence, Absorptions";
  const seoDesc = group === "templates"
    ? "Browse and buy professional Sierra Chart templates. Market Profile TPO, Footprint charts, Volume Profiles, Heatmap overlays, DOM depth of market tools. Instant download for traders."
    : "Buy custom Sierra Chart indicators for advanced trading. Orderflow Suite, Ratio & Volume Sequence, Absorptions Pack, and more. Professional-grade analysis tools.";
  const seoKeywords = group === "templates"
    ? "Sierra Chart templates download, market profile TPO template, footprint chart template, volume profile template, heatmap trading template, DOM Sierra Chart, chart template trading"
    : "Sierra Chart indicators download, orderflow indicators, volume sequence indicator, absorptions indicator, custom Sierra Chart studies, trading indicators buy";

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://sierrachartpro.com/" },
      { "@type": "ListItem", position: 2, name: group === "templates" ? "Templates" : "Indicators", item: `https://sierrachartpro.com/${group}` },
    ],
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO title={seoTitle} description={seoDesc} keywords={seoKeywords} jsonLd={breadcrumbJsonLd} />
      <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-6 flex-wrap" data-testid="breadcrumb">
        <Link href="/">
          <span className="cursor-pointer hover:text-foreground transition-colors">Home</span>
        </Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-foreground capitalize">{group}</span>
      </nav>

      <div className="mb-8">
        <div className="flex items-center gap-2 mb-1">
          <Zap className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium text-primary uppercase tracking-wider">
            {group}
          </span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="text-group-title">
          {title}
        </h1>
        <p className="text-muted-foreground max-w-2xl">{description}</p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <Skeleton className="w-full aspect-video rounded-t-md" />
              <CardContent className="p-4">
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((category) => (
            <CategoryCard key={category.id} category={category} />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="text-muted-foreground">
              No categories found. Check back soon!
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
